/**
 * AppLayout — two-column layout with Sidebar + main content.
 *
 * Used as a React Router layout route element in App.tsx (wraps <Outlet/>).
 * The Sidebar is self-contained (NavLink-based) and needs no shared layout
 * state.
 */

import { useEffect } from 'react'

import { ImpersonationBanner } from '@/components/ImpersonationBanner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { capitalizeOrgSlug, VANITY_ORG_SLUG } from '@/lib/vanity'

import { Sidebar } from './Sidebar'

interface AppLayoutProps {
  children: React.ReactNode
}

export function AppLayout({ children }: AppLayoutProps): React.JSX.Element {
  // Vanity subdomains (cartage.trainloop.ai) brand the tab with the org;
  // canonical hosts always read "TrainLoop Model Factory" regardless of
  // which team is selected. Uppercase the first letter only, matching
  // nginx's per-Host rewrite for scrapers (platform-v2-web.ts), so the
  // tab and the Slack link preview read the same.
  useEffect(() => {
    if (VANITY_ORG_SLUG === null) {
      document.title = 'TrainLoop Model Factory'
      return
    }
    document.title = `${capitalizeOrgSlug(VANITY_ORG_SLUG)} Model Factory`
  }, [])

  return (
    // Snappy tooltips app-wide — the Radix default 700ms reads as broken.
    <TooltipProvider delayDuration={150}>
      {/* Small-screen gate: the app layout isn't responsive, so below lg
          (1024px) show only this overlay instead of a crushed UI. Pure CSS —
          resizing past the breakpoint reveals the app with no reload. */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background p-8 lg:hidden">
        <div className="max-w-sm text-center">
          <img
            src="/Trainloop_Logo_Horizontal_Black.svg"
            alt="TrainLoop"
            className="mx-auto mb-6 h-8 w-auto"
          />
          <h1 className="font-serif text-xl font-semibold">
            This platform works best on a bigger screen
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Please open it on a laptop or desktop.
          </p>
        </div>
      </div>
      <div className="hidden h-screen lg:flex">
        <Sidebar />
        <div className="flex flex-1 flex-col overflow-hidden">
          <ImpersonationBanner />
          {/* `relative` keeps absolutely positioned descendants inside this
              scroll container. Radix Select/Checkbox inside a <form> render a
              hidden position:absolute bubble input; without a positioned
              ancestor it sits in document space below the fold and makes the
              whole document scroll (TRA-1152). */}
          <main className="relative flex-1 overflow-auto">{children}</main>
        </div>
      </div>
    </TooltipProvider>
  )
}
